import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const ChallengeItem = ({ title, description, isOpen, onClick }) => (
  <div 
    className={`rounded-xl border transition-all duration-300 overflow-hidden cursor-pointer ${
      isOpen ? 'bg-[#004048]/20 border-[#59d862]/30' : 'bg-white/[0.02] border-white/10 hover:border-white/20'
    }`}
    onClick={onClick}
  >
    <div className="px-5 py-4 flex items-center justify-between">
      <span className={`font-medium text-sm ${isOpen ? 'text-white' : 'text-white/80'}`}>{title}</span>
      <ChevronDown className={`w-4 h-4 text-white/50 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
    </div>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-32 pb-4 px-5' : 'max-h-0'}`}>
      <p className="text-white/60 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

const StatCard = ({ value, label }) => (
  <div className="p-5 rounded-2xl border transition-all duration-300 text-center bg-[#004048]/15 border-white/5 hover:border-white/15">
    <div className="text-3xl md:text-4xl font-bold mb-2 heading text-white">
      {value}
    </div>
    <div className="text-white/50 text-sm font-medium">{label}</div>
  </div>
);

const ProblemSection = () => {
  const [openChallenge, setOpenChallenge] = useState(0);

  const challenges = [
    {
      title: "Waiting 6+ months for commission",
      description: "After finding buyers, negotiating deals, and getting contracts signed, you wait months before seeing your commission."
    },
    {
      title: "Unable to invest in growth",
      description: "When cash is tied up in pending sales, you cannot invest in marketing during peak periods, hire new staff, or expand your operations."
    },
    {
      title: "Covering expenses despite successful sales",
      description: "Rent, salaries, marketing costs do not wait for completions. You cover business expenses while your earned commission sits in limbo."
    },
    {
      title: "1 in 3 sales falling through",
      description: "Even after months of work, sales can collapse at any point. Without legal protection, all that effort and the expected commission can vanish overnight."
    },
    {
      title: "Limited ability to scale",
      description: "Cash flow gaps prevent you from taking on new opportunities, opening new branches, or competing effectively with larger agencies."
    }
  ];

  return (
    <section className="relative py-16 md:py-20 bg-black overflow-hidden">
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-3 heading">
            The Cost of <span className="text-[#c4b9a6]">Uncertainty</span>
          </h2>
        </div>

        {/* Stats Row - Only 2 boxes without icons */}
        <div className="grid grid-cols-2 gap-4 mb-12 max-w-2xl mx-auto">
          <StatCard 
            value="1 in 3" 
            label="Sales fall through nationally" 
          />
          <StatCard 
            value="6+ Months" 
            label="Average wait for commission" 
          />
        </div>

        {/* Main Content - Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left Side - Problem Statement + CTAs */}
          <div className="space-y-5">
            <h3 className="text-2xl md:text-3xl font-bold text-white heading leading-tight">
              The Cash Flow Gap is<br/>
              <span className="text-red-400">Holding You Back</span>
            </h3>
            
            <p className="text-white/70 text-base leading-relaxed">
              For years, agents have invested time, money, and effort into transactions knowing there is a high chance they will never be paid. This broken status quo continues to drain cash flow, waste opportunity, and hold back growth, yet the industry has been conditioned to simply accept it.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link to="/get-paid" className="btn-primary px-6 py-3 justify-center">
                Get Paid Now <ChevronRight className="w-5 h-5" />
              </Link>
              <Link to="/apply" className="btn-secondary px-6 py-3 justify-center">
                Apply to Join
              </Link>
            </div>

            {/* Note Tab */}
            <div className="p-4 bg-[#004048]/20 border-l-2 border-[#c4b9a6] rounded-r-xl">
              <p className="text-white/60 text-sm leading-relaxed">
                <span className="text-[#c4b9a6] font-medium">Built by estate agents, for estate agents.</span> With over 30 years in the UK property industry, we understand these challenges firsthand. Gazeal Advance was created specifically to solve this problem.
              </p>
            </div>
          </div>

          {/* Right Side - Clickable Challenges */}
          <div className="space-y-3">
            {challenges.map((challenge, idx) => (
              <ChallengeItem 
                key={idx}
                title={challenge.title}
                description={challenge.description}
                isOpen={openChallenge === idx}
                onClick={() => setOpenChallenge(openChallenge === idx ? -1 : idx)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
